import React, { useContext, useEffect, useState } from 'react';

import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import { useFetch } from '../auth/useFetch';
import { useSnackbar } from 'notistack';

import UserContext from '../../contexts/UserContext';
import ProjectContext from '../../contexts/ProjectContext';

import FileDropBox from '../filedropbox/FileDropBox';
import SimpleTextField from '../display/SimpleTextField';

export default function UploadLUTDialog({ open, setOpen, retrieveLuts }) {
    const { konsolUser } = useContext(UserContext); 
    const projectContext = useContext(ProjectContext);
    const fetchWithAuth = useFetch();
    const { enqueueSnackbar } = useSnackbar();

    const [file, setFile] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (file && !name) {
            const base = file.name?.replace(/\.[^.]+$/, '') || '';
            setName(base);
        }
    }, [file, name]);

    const resetState = () => {
        setFile(null);
        setName('');
        setDescription('');
    };
    const handleClose = () => {
        setOpen(false);
        resetState();
    };
    const handleSubmit = async () => {
        if (uploading) return;
        if (!file || !name || !description) {
            enqueueSnackbar('Please provide file, name, and description.', { variant: 'warning' });
            return;
        }
        setUploading(true);
        setOpen(false);
        await uploadLut();
        resetState();
        setUploading(false);
    };

    const uploadLut = async () => {
        try {
            const formdata = new FormData();
            formdata.append('file_upload', file);
            formdata.append('name', name);
            formdata.append('description', description);
            formdata.append('user', `${konsolUser?.given_name ?? ''} ${konsolUser?.family_name ?? ''}`.trim());
            const requestOptions = {
                method: 'POST',
                body: formdata,
                redirect: 'follow',
                headers: {
                    'X-Project-ID': projectContext?.project_db
                },
            };
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/lut/`, requestOptions);
            if (response.ok) {
                enqueueSnackbar('LUT uploaded successfully', { variant: 'success' });
                retrieveLuts();
                return true;
            } else {
                let message = 'Failed to upload LUT';
                try {
                    const errorData = await response.json();
                    if (errorData.error) {
                        message = errorData.error;
                        if (errorData.allowed_extensions && Array.isArray(errorData.allowed_extensions)) {
                            message += ` Allowed extensions: ${errorData.allowed_extensions.join(', ')}`;
                        }
                    }
                } catch (_) {
                    // Fallback to text response if JSON parsing fails
                    try {
                        const text = await response.text();
                        if (text) message = `${message}: ${text}`;
                    } catch (_) {}
                }
                enqueueSnackbar(message, { variant: 'error' });
                return false;
            }
        } catch (error) {
            console.log('error', error);
            enqueueSnackbar('Unexpected error during LUT upload', { variant: 'error' });
            return false;
        }
    };

    return (
        <React.Fragment>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="lg"
                PaperProps={{
                    sx: {
                        width: "auto",
                        height: "auto",
                        backgroundColor: "#282828",
                    },
                }}
            >
                <DialogTitle>Upload LUT</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column'}}>
                        <Box m={1}>
                            <FileDropBox
                                titleText="Drop LUT files here or click to select"
                                UploadIcon={<CloudUpload sx={{ fontSize: 48, opacity: 0.4 }} />}
                                setFile={setFile}
                            />
                        </Box>
                        {file && (
                            <Box
                                m={1}
                                sx={{
                                    p: 3,
                                    backgroundColor: "#141414",
                                    color: "white",
                                    borderRadius: "8px",
                                    fontSize: "0.95rem",
                                    wordBreak: "break-all"
                                }}
                            >
                                {file.name}
                            </Box>
                        )}
                        <Box m={1}>
                            <SimpleTextField
                                label="Name"
                                onChange={(e) => setName(e.target.value)}
                                value={name}
                                required
                                width="100%"
                                size="small"
                                placeholder="Enter LUT name"
                                helperText={' '}
                            />
                            <SimpleTextField
                                label="Description"
                                onChange={(e) => setDescription(e.target.value)}
                                value={description}
                                required
                                width="100%"
                                multiline
                                rows={4}
                                placeholder="Describe this LUT"
                                helperText={' '}
                            />
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} sx={{ color: 'white' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} sx={{ color: 'white' }} disabled={uploading || !file || !name || !description}>
                        {uploading ? 'Uploading…' : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}